import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import TodayWorkoutCard from './components/TodayWorkoutCard';
import WorkoutCalendarPanel from './components/WorkoutCalendarPanel';
import AdherenceStatsPanel from './components/AdherenceStatsPanel';

const ClientWorkoutDashboard = () => {
  const { user } = useAuth();
  const [todayWorkout, setTodayWorkout] = useState(null);
  const [upcomingDays, setUpcomingDays] = useState([]);
  const [completions, setCompletions] = useState({});
  const [adherenceStats, setAdherenceStats] = useState({});
  const [selectedDay, setSelectedDay] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('today');

  const todayStr = new Date()?.toISOString()?.split('T')?.[0];

  useEffect(() => {
    if (user?.id) loadData();
  }, [user?.id]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Load active workout plan for this client
      const { data: plans } = await supabase?.from('client_workout_plans')?.select('id, name, start_date, end_date')?.eq('client_id', user?.id)?.eq('is_active', true)?.order('created_at', { ascending: false })?.limit(1);

      if (!plans?.length) {
        setIsLoading(false);
        return;
      }

      const planId = plans?.[0]?.id;
      const planName = plans?.[0]?.name;

      // Load workout days with exercises
      const { data: days } = await supabase?.from('client_workout_days')?.select(`
          id,
          scheduled_date,
          day_label,
          workout_plan_id,
          client_workout_plan_id
        `)?.eq('client_workout_plan_id', planId)?.order('scheduled_date', { ascending: true });

      if (!days?.length) {
        setIsLoading(false);
        return;
      }

      // Load exercises for each day's workout plan
      const enrichedDays = await Promise.all(
        days?.map(async (day) => {
          if (!day?.workout_plan_id) return { ...day, workoutPlanExercises: [], planName };

          const { data: exercises } = await supabase?.from('workout_plan_exercises')?.select('*, exercise:exercises(*)')?.eq('workout_plan_id', day?.workout_plan_id)?.order('order_index');

          return { ...day, workoutPlanExercises: exercises || [], planName };
        })
      );

      // Load completions for today
      const todayDay = enrichedDays?.find(d => d?.scheduled_date === todayStr);
      if (todayDay) {
        const { data: todayCompletions } = await supabase?.from('client_workout_completions')?.select('*')?.eq('client_workout_day_id', todayDay?.id)?.eq('client_id', user?.id);

        const completionMap = {};
        todayCompletions?.forEach(c => {
          completionMap[c?.workout_plan_exercise_id] = c;
        });
        setCompletions(completionMap);
        setTodayWorkout(todayDay);
      }

      setUpcomingDays(enrichedDays);

      // Calculate adherence stats
      const now = new Date();
      const weekStart = new Date(now);
      weekStart?.setDate(now?.getDate() - now?.getDay() + 1);
      weekStart?.setHours(0, 0, 0, 0);
      const monthStart = new Date(now?.getFullYear(), now?.getMonth(), 1);

      const weekDays = enrichedDays?.filter(d => {
        const date = new Date(d?.scheduled_date);
        return date >= weekStart && date <= now;
      });
      const monthDays = enrichedDays?.filter(d => {
        const date = new Date(d?.scheduled_date);
        return date >= monthStart && date <= now;
      });

      // Load all completions for stats
      const allDayIds = [...weekDays, ...monthDays]?.map(d => d?.id);
      if (allDayIds?.length > 0) {
        const { data: allCompletions } = await supabase?.from('client_workout_completions')?.select('*')?.in('client_workout_day_id', allDayIds)?.eq('client_id', user?.id)?.eq('completed', true);

        const completedDayIds = new Set(allCompletions?.map(c => c?.client_workout_day_id));

        setAdherenceStats({
          weeklyAssigned: weekDays?.length,
          weeklyCompleted: weekDays?.filter(d => completedDayIds?.has(d?.id))?.length,
          monthlyAssigned: monthDays?.length,
          monthlyCompleted: monthDays?.filter(d => completedDayIds?.has(d?.id))?.length,
          currentStreak: calculateStreak(enrichedDays, allCompletions || []),
        });
      }
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateStreak = (days, completions) => {
    const completedDayIds = new Set(completions?.map(c => c?.client_workout_day_id));
    let streak = 0;
    const today = new Date();
    today?.setHours(0, 0, 0, 0);

    const sortedDays = [...days]
      ?.filter(d => new Date(d?.scheduled_date) <= today)
      ?.sort((a, b) => new Date(b?.scheduled_date) - new Date(a?.scheduled_date));

    for (const day of sortedDays) {
      if (completedDayIds?.has(day?.id)) {
        streak++;
      } else {
        break;
      }
    }
    return streak;
  };

  const handleToggleCompletion = async (exerciseId, completed) => {
    if (!todayWorkout) return;

    const existing = completions?.[exerciseId];

    try {
      if (existing) {
        const { error } = await supabase?.from('client_workout_completions')?.update({ completed, completed_at: completed ? new Date()?.toISOString() : null })?.eq('id', existing?.id);

        if (!error) {
          setCompletions(prev => ({
            ...prev,
            [exerciseId]: { ...existing, completed, completed_at: completed ? new Date()?.toISOString() : null },
          }));
        }
      } else {
        const { data, error } = await supabase?.from('client_workout_completions')?.insert({
            client_workout_day_id: todayWorkout?.id,
            workout_plan_exercise_id: exerciseId,
            client_id: user?.id,
            completed,
            completed_at: completed ? new Date()?.toISOString() : null,
          })?.select()?.single();

        if (!error && data) {
          setCompletions(prev => ({ ...prev, [exerciseId]: data }));
        }
      }
    } catch (err) {
      console.error('Toggle completion error:', err);
    }
  };

  const handleSelectDay = async (day) => {
    setSelectedDay(day);
    setActiveTab('calendar');
  };

  const completedCount = todayWorkout?.workoutPlanExercises?.filter(ex => completions?.[ex?.id]?.completed)?.length || 0;
  const totalCount = todayWorkout?.workoutPlanExercises?.length || 0;

  return (
    <>
      <Helmet>
        <title>My Workouts - Form Forge</title>
      </Helmet>
      <div className="min-h-screen" style={{ background: '#0A0A0B' }}>
        <Header />
        <main className="container mx-auto px-4 py-6">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1
                className="text-2xl font-extrabold tracking-tight"
                style={{ fontFamily: "'Inter Tight', 'Inter', sans-serif", color: '#fff', letterSpacing: '-0.02em' }}
              >
                My Workouts
              </h1>
              <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.45)' }}>
                {new Date()?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </p>
            </div>
            {totalCount > 0 && (
              <div
                className="flex items-center gap-2 px-4 py-2 rounded-xl"
                style={{
                  background: 'rgba(170,255,0,0.08)',
                  border: '1px solid rgba(170,255,0,0.25)',
                  boxShadow: '0 0 12px rgba(170,255,0,0.12)',
                }}
              >
                <Icon name="Dumbbell" size={16} style={{ color: '#AAFF00' }} />
                <span
                  className="text-sm font-bold"
                  style={{ fontFamily: "'Inter Tight', 'Inter', sans-serif", color: '#AAFF00' }}
                >
                  {completedCount}/{totalCount} today
                </span>
              </div>
            )}
          </div>

          {/* Mobile Tab Navigation */}
          <div
            className="flex gap-1 mb-5 p-1 rounded-xl lg:hidden"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            {[
              { id: 'today', label: "Today's Workout", icon: 'Dumbbell' },
              { id: 'calendar', label: 'Schedule', icon: 'Calendar' },
              { id: 'stats', label: 'Progress', icon: 'TrendingUp' },
            ]?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-semibold rounded-lg transition-all"
                style={{
                  background: activeTab === tab?.id ? 'rgba(255,255,255,0.08)' : 'transparent',
                  color: activeTab === tab?.id ? '#fff' : 'rgba(255,255,255,0.4)',
                  fontFamily: "'Inter Tight', 'Inter', sans-serif",
                }}
              >
                <Icon name={tab?.icon} size={14} />
                <span className="hidden sm:inline">{tab?.label}</span>
              </button>
            ))}
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:grid lg:grid-cols-12 gap-5">
            {/* Left: Today's Workout */}
            <div className="col-span-5">
              <TodayWorkoutCard
                todayWorkout={todayWorkout}
                completions={completions}
                onToggleCompletion={handleToggleCompletion}
                isLoading={isLoading}
              />
            </div>

            {/* Center: Calendar */}
            <div className="col-span-4">
              <WorkoutCalendarPanel
                upcomingDays={upcomingDays}
                onSelectDay={handleSelectDay}
                selectedDayId={selectedDay?.id}
              />
            </div>

            {/* Right: Adherence */}
            <div className="col-span-3">
              <AdherenceStatsPanel stats={adherenceStats} />
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="lg:hidden">
            {activeTab === 'today' && (
              <TodayWorkoutCard
                todayWorkout={todayWorkout}
                completions={completions}
                onToggleCompletion={handleToggleCompletion}
                isLoading={isLoading}
              />
            )}
            {activeTab === 'calendar' && (
              <div className="space-y-4">
                <WorkoutCalendarPanel
                  upcomingDays={upcomingDays}
                  onSelectDay={handleSelectDay}
                  selectedDayId={selectedDay?.id}
                />
                {selectedDay && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-2xl p-4"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)' }}
                  >
                    <h3 className="text-sm font-bold mb-3" style={{ color: '#fff', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>
                      {selectedDay?.dayLabel} — {new Date(selectedDay?.scheduled_date)?.toLocaleDateString()}
                    </h3>
                    <div className="space-y-2">
                      {selectedDay?.workoutPlanExercises?.map((ex, idx) => (
                        <div
                          key={ex?.id}
                          className="flex items-center gap-3 p-2.5 rounded-xl"
                          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
                        >
                          <span
                            className="w-5 h-5 rounded-md flex items-center justify-center text-xs font-bold flex-shrink-0"
                            style={{ background: 'rgba(0,212,255,0.15)', color: '#00D4FF' }}
                          >
                            {idx + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold truncate" style={{ color: '#fff' }}>{ex?.exercise?.name}</p>
                            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>{ex?.sets}×{ex?.reps} · {ex?.rest_seconds}s rest</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>
            )}
            {activeTab === 'stats' && (
              <AdherenceStatsPanel stats={adherenceStats} />
            )}
          </div>

          {/* No Plan State */}
          {!isLoading && !todayWorkout && upcomingDays?.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-8 text-center py-16 rounded-2xl"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', backdropFilter: 'blur(20px)' }}
            >
              <div
                className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4"
                style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.2)' }}
              >
                <Icon name="Dumbbell" size={36} style={{ color: '#00D4FF' }} />
              </div>
              <h2 className="text-xl font-bold mb-2" style={{ color: '#fff', fontFamily: "'Inter Tight', 'Inter', sans-serif" }}>
                No Workout Plan Yet
              </h2>
              <p className="text-sm max-w-sm mx-auto" style={{ color: 'rgba(255,255,255,0.45)' }}>
                Your trainer hasn't assigned a workout plan yet. Check back soon or reach out to your trainer.
              </p>
            </motion.div>
          )}
        </main>
      </div>
    </>
  );
};

export default ClientWorkoutDashboard;
